import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  Code, 
  Smartphone, 
  Database, 
  Cloud, 
  Palette, 
  Zap,
  ArrowRight 
} from 'lucide-react';

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const services = [
    {
      icon: Code,
      title: 'React Development',
      description: 'Modern, responsive web applications built with React, Next.js, and Tailwind CSS.',
      features: ['React/Next.js', 'Tailwind CSS', 'TypeScript', 'REST APIs'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Smartphone,
      title: 'Mobile Development',
      description: 'Cross-platform mobile applications using React Native and modern mobile technologies.',
      features: ['React Native', 'Mobile Apps', 'PWA', 'Hybrid Apps'],
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Database,
      title: 'Java Backend',
      description: 'Robust server-side solutions with Java Spring Boot and MySQL database.',
      features: ['Java/Spring Boot', 'MySQL', 'REST APIs', 'Microservices'],
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Code,
      title: 'Frontend Development',
      description: 'Beautiful user interfaces using HTML, CSS, JavaScript, and modern frameworks.',
      features: ['HTML/CSS/JS', 'Responsive Design', 'Modern UI', 'Accessibility'],
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Palette,
      title: 'UI/UX Design',
      description: 'Intuitive user interfaces designed for optimal user experience and engagement.',
      features: ['Figma', 'Adobe XD', 'Prototyping', 'User Testing'],
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: Zap,
      title: 'Full-Stack Solutions',
      description: 'Complete web and mobile solutions from frontend to backend development.',
      features: ['React + Java', 'MySQL Database', 'Deployment', 'Maintenance'],
      color: 'from-yellow-500 to-orange-500'
    }
  ];

  return (
    <section id="services" className="section-padding bg-gray-50">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Services We Offer
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive development services to bring your digital ideas to life. 
            From concept to deployment, our team handles every aspect of your project.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="card p-8 group cursor-pointer"
            >
              {/* Icon */}
              <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <service.icon className="w-8 h-8 text-white" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {service.title}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {service.description}
              </p>

              {/* Features */}
              <div className="space-y-2 mb-6">
                {service.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-accent-600 rounded-full"></div>
                    <span className="text-sm text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="flex items-center text-accent-600 font-medium group-hover:text-accent-700 transition-colors">
                <span>Learn More</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Start Your Project?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Let's discuss your ideas and create something amazing together. 
              Our team is here to help you bring your vision to life.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary"
            >
              Get Started Today
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services; 